import { type LuminarySettings, VideoData } from '@lumen5/luminary';

import { SourceEnum } from 'js/src/client';

// all media sources that users need to pay to access (unlimited use & credit)
export const PREMIUM_SOURCES = new Set([
    SourceEnum.SOURCE_GETTY,
    SourceEnum.SOURCE_GETTY_UNLIMITED,
    SourceEnum.SOURCE_SHUTTERSTOCK_ESSENTIALS,
    SourceEnum.SOURCE_SHUTTERSTOCK_PREMIUM,
    SourceEnum.SOURCE_SHUTTERSTOCK_UNLIMITED,
    SourceEnum.SOURCE_SHUTTERSTOCK_EDITORIAL,
    SourceEnum.SOURCE_SHUTTERSTOCK_CHOICES,
]);

export const RENDERING_BLOCKING_SOURCES = [
    SourceEnum.SOURCE_GETTY,
    SourceEnum.SOURCE_SHUTTERSTOCK_PREMIUM,
    SourceEnum.SOURCE_SHUTTERSTOCK_EDITORIAL,
];

export const DEFAULT_LUMINARY_CONFIG: LuminarySettings = {
    mediaSizeToUse: VideoData.PREVIEW_MEDIA_SIZE,
};

export const ORDERED_TEXT_POSITIONS = [
    VideoData.TOP_LEFT_TEXT_POSITION,
    VideoData.TOP_CENTER_TEXT_POSITION,
    VideoData.TOP_RIGHT_TEXT_POSITION,
    VideoData.CENTER_LEFT_TEXT_POSITION,
    VideoData.CENTER_TEXT_POSITION,
    VideoData.CENTER_RIGHT_TEXT_POSITION,
    VideoData.BOTTOM_LEFT_TEXT_POSITION,
    VideoData.BOTTOM_CENTER_TEXT_POSITION,
    VideoData.BOTTOM_RIGHT_TEXT_POSITION,
];

export const SCREEN_WIDTH_THRESHOLD = 900; // in px

export enum LuminaryResolutionPreset {
    HIGH = 'high',
    NORMAL = 'normal',
}

const LUMINARY_RESOLUTION_PRESETS = {
    [LuminaryResolutionPreset.NORMAL]: {
        largeScreen: 360,
        smallScreen: 200,
    },
    [LuminaryResolutionPreset.HIGH]: {
        largeScreen: 720,
        smallScreen: 360,
    },
};

// largeScreen 562.5 = 1000 / (16/9), matching the xl-screen landscape-panel-max-width
// cap in _mixins.scss so the GPU buffer maps 1:1 to the canvas. The correct value for
// smallScreen is 450 -- however, we use 360 since we have been using it for a long time
// and it keeps memory/compute low on weaker mobile devices.
const SHARE_PAGE_LUMINARY_RESOLUTION = {
    largeScreen: 562.5,
    smallScreen: 360,
};

function isSmallScreen(): boolean {
    return !!window.screen && window.screen.availWidth <= SCREEN_WIDTH_THRESHOLD;
}

/**
 * Determines the appropriate resolutionValue Luminary should be created with based
 * on the available width of the user's screen
 */
export function getLuminaryResolutionFromAvailScreenWidth(
    resolutionPreset: LuminaryResolutionPreset = LuminaryResolutionPreset.NORMAL,
): number {
    return isSmallScreen()
        ? LUMINARY_RESOLUTION_PRESETS[resolutionPreset].smallScreen
        : LUMINARY_RESOLUTION_PRESETS[resolutionPreset].largeScreen;
}

/**
 * Determines the appropriate resolutionValue for the share page based on
 * the available width of the user's screen.
 */
export function getSharePageLuminaryResolution(): number {
    return isSmallScreen()
        ? SHARE_PAGE_LUMINARY_RESOLUTION.smallScreen
        : SHARE_PAGE_LUMINARY_RESOLUTION.largeScreen;
}

export const TEXT_EDITING_OVERFLOW_EVENT = 'nte-text-overflow';
